<template>
  <div
    ref="headerSearch"
    v-click-outside="closeSearchList"
    class="headerSearch"
    :class="{
      'headerSearch--active': openDropdown,
      'headerSearch--mobileOpen': mobileOpen,
    }"
  >
    <div class="headerSearch__inner">
      <div class="headerSearch__content">
        <input
          :ref="`searchInput${mobile ? '-mobile' : ''}`"
          :value="string"
          class="headerSearch__input"
          placeholder="Szukaj np. fotel biurowy"
          @keyup.enter="goToSearch"
          @click="openMobileDropdown($event)"
          @input="search"
        />

        <button
          class="headerSearch__button"
          :class="{ 'headerSearch__button--mobileSearch': mobile }"
          @click="goToSearch"
        >
          <span class="icon-search" />
        </button>
        <button
          v-if="mobile"
          class="headerSearch__button headerSearch__button--mobileClose"
          @click="closeMobileSearch"
        >
          <span class="icon-line-close" />
        </button>
        <div
          v-show="
            data && Object.keys(data).length && (data.categories.length || data.products.length)
          "
          ref="searchDropdown"
          class="headerSearch__dropdown prettyScrollbar"
        >
          <div class="headerSearch__dropdownContent">
            <div class="headerSearch__categories">
              <div class="headerSearch__category">
                <div class="headerSearch__categoryName">Kategorie</div>
                <ul class="headerSearch__categoryList">
                  <li
                    v-for="item in data.categories"
                    :key="item.id"
                    class="headerSearch__categoryItem"
                  >
                    <nuxt-link
                      class="headerSearch__categoryLink"
                      :to="localePath({ name: 'category-slug', params: { slug: item.slug } })"
                      @click.native="
                        clickLink(
                          localePath({ name: 'category-slug', params: { slug: item.slug } }),
                          item.id,
                        )
                      "
                    >
                      <span class="headerSearch__categoryLinkIcon icon-category" />{{ item.name }}
                    </nuxt-link>
                  </li>
                </ul>
              </div>
              <div class="headerSearch__category">
                <div class="headerSearch__categoryName">Produkty</div>
                <ul class="headerSearch__productList">
                  <li
                    v-for="item in data.products"
                    :key="item.id"
                    class="headerSearch__productItem"
                  >
                    <nuxt-link
                      class="headerSearch__productLink"
                      :to="localePath({ name: 'product-slug', params: { slug: item.slug } })"
                      @click.native="gTagProductClick(item)"
                    >
                      <div class="headerSearch__productImage">
                        <new-image-box
                          :alt="item.name"
                          :src="item.mainPictureUrl150x150"
                          :width="60"
                          :height="60"
                          image-params="w_60,h_60"
                        />
                      </div>
                      <div class="headerSearch__productDesc">
                        <div v-line-clamp:20="1" class="headerSearch__productName">
                          {{ item.name }}
                        </div>
                        <div class="headerSearch__productCategory">
                          {{ item.category.name }}
                        </div>
                      </div>
                      <div class="headerSearch__productPrice">
                        <span>
                          {{ item.priceObject.currencyPrefix }}
                          <b>{{
                            showNetPrices
                              ? item.priceObject.netFormatted
                              : item.priceObject.grossFormatted
                          }}</b>
                          {{ item.priceObject.currencyPostfix }}
                          {{ showNetPrices ? $t('net') : $t('gross') }}
                          <span
                            v-if="item.priceBeforeDiscountObject"
                            class="headerSearch__productPriceSmall"
                          >
                            {{ item.priceBeforeDiscountObject.currencyPrefix }}
                            {{
                              showNetPrices
                                ? item.priceBeforeDiscountObject.netFormatted
                                : item.priceBeforeDiscountObject.grossFormatted
                            }}
                            {{ item.priceBeforeDiscountObject.currencyPostfix }}
                            {{ showNetPrices ? $t('net') : $t('gross') }}
                          </span>
                        </span>
                      </div>
                    </nuxt-link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import slugify from 'slugify';
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import NewImageBox from '@/components/NewImageBox';

const dropdownOptions = {
  maxHeight: 410,
};

export default {
  name: 'HeaderSearch',
  components: {
    NewImageBox,
    Multiselect,
  },
  props: {
    mobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectValue: null,
      openDropdown: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['showNetPrices']),
    ...mapState({
      isMobileSearchOpened: state => state.layout.isMobileSearchOpened,
      autocompleteCategoryMenuData: state => state.layout.autocompleteCategoryMenuData,
      string: state => state.autocomplete.string,
      category: state => state.autocomplete.category,
      data: state => state.autocomplete.data,
    }),

    mobileOpen() {
      return this.mobile && this.isMobileSearchOpened;
    },
  },
  watch: {
    mobileOpen(n, o) {
      if (n) {
        this.$refs['searchInput-mobile'].focus();
        this.openSearchList();
      }
    },
  },
  mounted() {
    this.setString(this.$route.query.query);
    if (this.$route.query.query) {
      this.fetchAutocomplete();
    }

    this.closeSearchList();

    window.addEventListener('resize', () => {
      if (this.mobileOpen) this.openSearchList();
    });
  },
  methods: {
    ...mapMutations({
      toggleSearch: 'layout/toggleMobileSearchOpened',
      setString: 'autocomplete/setString',
      setCategory: 'autocomplete/setCategory',
    }),

    ...mapActions({
      fetchAutocomplete: 'autocomplete/fetchAutocomplete',
      submitHeaderForm: 'productsFilters/submitHeaderForm',
      resetFilters: 'productsFilters/resetFilters',
    }),

    openMobileDropdown(e) {
      if (this.data) {
        this.openSearchList();
      }
      if (window.innerWidth <= 768 && !this.mobile) {
        e.currentTarget.blur();
        this.toggleSearch();
        this.$refs.searchInput.blur();
      }
    },
    openSearchList() {
      const dropdown = this.$refs.searchDropdown;
      if (!dropdown) return;
      if (window.innerWidth > 768) {
        dropdown.style.maxHeight =
          dropdown.scrollHeight < dropdownOptions.maxHeight
            ? `${dropdown.scrollHeight}px`
            : `${dropdownOptions.maxHeight}px`;
      } else if (dropdown) {
        dropdown.style.maxHeight = `calc((var(--vh, 1vh) * 100) - 48px - 4rem)`;
        const viewport = window.innerHeight * 0.01;
        dropdown.style.setProperty('--vh', `${viewport}px`);
      }

      this.openDropdown = true;
    },
    closeSearchList() {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur();
      }
      const dropdown = this.$refs.searchDropdown;
      if (!dropdown) return;
      dropdown.style.maxHeight = '0px';
      this.openDropdown = false;
    },
    closeMobileSearch() {
      if (this.$refs['searchInput-mobile']) {
        this.$refs['searchInput-mobile'].blur();
      }
      if (this.isMobileSearchOpened) this.toggleSearch();
    },
    async search(e) {
      try {
        const value = e.target.value;
        this.setString(value);
        await this.fetchAutocomplete();
        if (value === '') {
          this.closeSearchList();
        } else {
          this.openSearchList();
        }
      } catch {}
    },
    clickLink(link, catId) {
      this.closeSearchList();
      this.closeMobileSearch();
      if (catId && this.$route.name.split('___')[0] === 'search') {
        this.submitHeaderForm({
          type: 'search',
          value: {
            categories: [catId],
          },
        });
      } else {
        this.$router.push(link);
      }
    },
    changeMultiselect(val) {
      this.selectValue = val;
      if (val) {
        this.setCategory(val.id);
      } else {
        this.setCategory(null);
      }
      if (typeof this.string === 'string' && this.string !== '') {
        this.fetchAutocomplete();
      }
    },
    async goToSearch() {
      if (
        (this.string === '' || this.string === undefined || this.string === null) &&
        this.$route.name.split('___')[0] !== 'search'
      )
        return;

      await this.$axios
        .get(
          `${this.$config.baseOfixApi}/e-commerce/v1/contents/ats/${slugify(this.string, {
            lower: true,
          })}`,
          {
            useCache: true,
          },
        )
        .then(response => {
          this.$router.push(
            this.localePath({
              name: 'ats-slug',
              params: {
                slug: slugify(this.string, {
                  lower: true,
                }),
              },
            }),
          );
        })
        .catch(e => {
          if (this.$route.name.split('___')[0] === 'search') {
            this.submitHeaderForm({
              type: 'search',
              value: {
                categories: this.selectValue ? [this.selectValue.id] : null,
                query: this.string,
              },
            });
          } else {
            this.$router.push(
              this.localePath({
                name: 'search',
                query: {
                  'categories[]': this.selectValue ? [this.selectValue.id] : null,
                  query: this.string,
                },
              }),
            );
          }
        });

      this.closeSearchList();
      this.closeMobileSearch();
    },
    gTagProductClick(item) {
      this.closeSearchList();
      this.closeMobileSearch();

      const categories = item.category.namesInPath.reduce((acc, curr, index) => {
        acc['category' + (index === 0 ? '' : index + 1)] = curr;
        return acc;
      }, {});

      this.$gtm.push({ ecommerce: null });
      this.$gtm.push({
        event: 'select_item',
        ecommerce: {
          currency: 'PLN',
          item_list_id: 'header_search',
          item_list_name: 'Header search',
          items: [
            {
              item_id: item.id,
              item_name: item.name,
              discount: item.priceBeforeDiscountObject
                ? this.$helper.calculateDiscount(
                    item.priceBeforeDiscountObject.gross,
                    item.priceObject.gross,
                  )
                : 0,
              brand: item.brand ? item.brand.name : '',
              ...categories,
              item_list_id: 'header_search',
              item_list_name: 'Header search',
              price: item.priceObject.gross,
              variant: '',
              quantity: 1,
            },
          ],
        },
      });
    },
  },
};
</script>

<style lang="scss">
.headerSearch {
  $root: &;
  position: relative;
  flex: 1;
  height: 56px;

  @include rwd('small-laptop') {
    flex: 1;
    max-width: 100%;
    margin-right: 2rem;
  }

  &--active {
    #{$root}__content {
      box-shadow: 0 10px 20px rgba(23, 25, 51, 0.1);
    }

    #{$root}__dropdown {
      border-top: 1px solid $cAddLight;
    }
  }

  &__content {
    position: absolute;
    display: flex;
    padding: 3px;
    left: 0;
    top: 0;
    width: 100%;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    background-color: $cBg;
    box-shadow: 0 1px 1px rgba($cMainDark, 0.05);
    border-radius: 4px;
    transition: box-shadow $tr;
  }

  &__input {
    flex: 0 0 100%;
    max-width: 100%;
    padding-right: 6rem;
    font-size: 1.4rem;
    line-height: 1.8rem;
    color: $cMainDark;
    height: 50px;
    box-shadow: 0 1px 1px rgba($cMainDark, 0.1);
    border: 1px solid $cAddLight;
    background-color: $cWhite;
    padding-left: 2rem;

    @include rwd('large-tablet') {
      flex: 0 0 100%;
      max-width: 100%;
      border-right: 0;
      padding-left: 1.5rem;
      padding-right: 6rem;
    }

    &::placeholder {
      color: $cMainLight;
    }
  }

  &__select {
    flex: 0 0 40%;
    max-width: 40%;
    height: 48px;

    @include rwd('large-tablet') {
      display: none;
    }

    .multiselect {
      min-height: 48px;

      &__select {
        right: 45px;
        height: 48px;

        &:before {
          color: $cMainLight;
          font-size: 8px;
        }
      }

      &__single {
        white-space: nowrap;
      }

      &__tags {
        min-height: 48px;
        padding: 0 8rem 0 2rem;
        border: 0;
        border-radius: 0;
      }
    }
  }

  &__button {
    position: absolute;
    z-index: 100;
    top: 8px;
    right: 8px;
    width: 40px;
    height: 40px;
    background-color: $cSec;
    border-radius: 4px;
    color: $cWhite;
    font-size: 16px;
    transition: background-color $tr;

    &:hover {
      background-color: $cSecHover;
    }

    &--mobileSearch {
      right: 5rem;
    }

    &--mobileClose {
      background-color: $cWhite;
      color: $cMain;
    }
  }

  &__dropdown {
    flex: 0 0 100%;
    overflow: auto;
    max-height: calc(100vh - 48px - 4rem);
    width: 100%;
    background-color: $cWhite;
    border-top: 0;
    transition: border-top $tr, max-height $tr;
    padding-right: 6px;
  }

  &__dropdownContent {
    padding: 2rem 2.5rem 1.5rem 2rem;

    @include rwd('tablet') {
      padding: 2rem 1.5rem 1.5rem 2rem;
    }

    @include rwd('large-phone') {
      padding: 1.5rem 0 1.5rem 0;
    }
  }

  &__categoryName {
    font-size: 1.4rem;
    line-height: 1.8rem;
    font-weight: 700;
    margin-bottom: 1.6rem;
  }

  &__categoryList {
    margin-bottom: 2rem;
  }

  &__categoryLink {
    margin-bottom: 0.9rem;
    display: flex;
    align-content: center;
    font-size: 1.4rem;
    line-height: 1.8rem;
    transition: color $tr;

    &:hover {
      color: $cSec;

      #{$root}__categoryLinkIcon {
        color: $cSec;
      }
    }
  }

  &__categoryLinkIcon {
    margin-right: 1.6rem;
    color: $cAddDark02;
    font-size: 1.6rem;
    transition: color $tr;
  }

  &__productLink {
    display: flex;
    margin-bottom: 5px;
    align-items: center;
    width: 100%;

    &:hover {
      #{$root}__productName,
      #{$root}__productCategory,
      #{$root}__productPrice {
        color: $cSec;
      }
    }
  }

  &__productImage {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 2.5rem;
    width: 55px;
    height: 55px;
    padding: 3px;

    @include rwd('tablet') {
      margin-right: 1.5rem;
    }
  }

  &__productDesc {
    flex: 0 0 48%;
    max-width: 48%;

    @include rwd('tablet') {
      flex: 1;
      max-width: 100%;
      padding-right: 1rem;
    }
  }

  &__productName {
    font-size: 1.4rem;
    line-height: 1.8rem;
    margin-bottom: 3px;
    transition: color $tr;
    text-align: left;
    word-break: break-all !important;
  }

  &__productCategory {
    color: $cAddDark02;
    font-size: 1.3rem;
    line-height: 1.6rem;
    text-align: left;
    transition: color $tr;
  }

  &__productPrice {
    font-family: $fSecond;
    margin-left: auto;
    font-size: 1.4rem;
    line-height: 1.7rem;
    transition: color $tr;

    @include rwd('tablet') {
      text-align: right;
      font-size: 1.2rem;
      line-height: 1.5rem;
    }

    b {
      font-size: 1.6rem;
      line-height: 1.9rem;
      font-weight: 500;

      @include rwd('tablet') {
        display: block;
        font-size: 1.4rem;
        line-height: 1.5rem;
      }
    }
  }

  &__productPriceSmall {
    display: block;
    text-align: right;
    font-size: 1.2rem;
    text-decoration: line-through;
    color: $cAddDark02;
  }

  &--mobile {
    display: none;
    position: fixed;
    background-color: $cWhite;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: $zPopup;
    opacity: 0;
    pointer-events: none;
    transition: opacity $tr;

    @include rwd('tablet') {
      display: block;
    }

    #{$root}__inner {
      padding: 2rem;
    }

    #{$root}__content {
      position: relative;
    }
  }

  &--mobileOpen {
    opacity: 1;
    pointer-events: all;
  }
}
</style>
